import { useState } from 'react';
import { DataTable } from '@/components/tables';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, AlertTriangle, Plus, Minus } from 'lucide-react';
import { cn, formatDate } from '@/lib/utils';
import { mockProducts } from '@/lib/mock-data';

const columns = [
  {
    accessorKey: 'image',
    header: 'Product',
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-muted overflow-hidden">
          {row.original.image ? (
            <img src={row.original.image} alt={row.original.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
        </div>
        <div>
          <p className="font-medium">{row.original.name}</p>
          <p className="text-xs text-muted-foreground">{row.original.sku}</p>
        </div>
      </div>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'stock',
    header: 'Current Stock',
    cell: ({ row }) => {
      const stock = row.original.stock;
      const isLow = stock <= 5;
      const isOut = stock === 0;
      return (
        <div className="flex items-center gap-2">
          {isLow && !isOut && <AlertTriangle className="w-4 h-4 text-yellow-500" />}
          {isOut && <AlertTriangle className="w-4 h-4 text-red-500" />}
          <span className={cn(
            'font-semibold',
            isOut && 'text-red-500',
            isLow && !isOut && 'text-yellow-500'
          )}>
            {stock}
          </span>
          <span className="text-muted-foreground">units</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'reorderLevel',
    header: 'Reorder Level',
    cell: () => <span className="text-muted-foreground">10 units</span>,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const stock = row.original.stock;
      if (stock === 0) return <Badge variant="destructive">Out of Stock</Badge>;
      if (stock <= 5) return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">Low Stock</Badge>;
      return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">In Stock</Badge>;
    },
  },
  {
    accessorKey: 'lastUpdated',
    header: 'Last Updated',
    cell: () => <span className="text-muted-foreground">{formatDate(new Date())}</span>,
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <Dialog>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            Adjust Stock
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Stock - {row.original.name}</DialogTitle>
            <DialogDescription>
              Update the inventory level for this product.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center gap-4">
              <Button variant="outline" size="icon">
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                type="number"
                defaultValue={row.original.stock}
                className="w-24 text-center text-lg"
              />
              <Button variant="outline" size="icon">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Reason for adjustment</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="received">Stock Received</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem>
                  <SelectItem value="damaged">Damaged</SelectItem>
                  <SelectItem value="returned">Returned</SelectItem>
                  <SelectItem value="correction">Inventory Correction</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input placeholder="Add any notes..." />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline">Cancel</Button>
            <Button>Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    ),
  },
];

export default function InventoryPage() {
  const [products] = useState(mockProducts);

  const lowStockCount = products.filter((p) => p.stock <= 5 && p.stock > 0).length;
  const outOfStockCount = products.filter((p) => p.stock === 0).length;

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Inventory</h1>
          <p className="text-muted-foreground">Track and manage your stock levels</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl border bg-card">
          <p className="text-sm text-muted-foreground mb-1">Total Products</p>
          <p className="text-2xl font-bold">{products.length}</p>
        </div>
        <div className="p-4 rounded-xl border bg-yellow-50 dark:bg-yellow-900/20">
          <p className="text-sm text-yellow-600 dark:text-yellow-400 mb-1">Low Stock</p>
          <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">{lowStockCount}</p>
        </div>
        <div className="p-4 rounded-xl border bg-red-50 dark:bg-red-900/20">
          <p className="text-sm text-red-600 dark:text-red-400 mb-1">Out of Stock</p>
          <p className="text-2xl font-bold text-red-700 dark:text-red-300">{outOfStockCount}</p>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={products}
        searchPlaceholder="Search inventory..."
      />
    </div>
  );
}
